import cn from 'classnames';
import { MEDIA_QUERIES, useMediaQuery } from '@utils/hooks/use-media-query.tsx';
import Logo from '@components/shared/logo.tsx';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { css, StyleSheet } from 'aphrodite';
import { useLocation, useNavigate } from 'react-router-dom';
import Button from '@components/atoms/ui-kit/button.tsx';
import { Tooltip } from '@components/atoms/ui-kit/tooltip.tsx';
import {
  ActivitySquare,
  ExternalLink,
  Menu,
  MessageSquareMore
} from 'lucide-react';
import { useMemo } from 'react';
import { PATHS } from '../../../router/paths.ts';
import { IS_ON_PREM } from '@utils/constants/app-constants.ts';

export default function TopNavSharedPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const [largeTabletAndAbove] = useMediaQuery(
    `(min-width: ${MEDIA_QUERIES.LARGE_TABLET})`
  );
  const { getColour, theme } = useThemeContext();

  const bg = getColour('firstLayer');
  const border = getColour('border');
  const text = getColour('normalText');
  const icon = getColour('icon');
  const active = getColour('activeText');

  const styles = useMemo(
    () =>
      StyleSheet.create({
        container: {
          backgroundColor: bg,
          color: text,
          borderBottom: `1px solid ${border}`
        }
      }),
    [getColour]
  );

  const styleVars: React.CSSProperties = useMemo(
    () => ({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ['--tn-bg' as any]: bg,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ['--tn-border' as any]: border,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ['--tn-text' as any]: text,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ['--tn-icon' as any]: icon,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ['--tn-active' as any]: active,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ['--tn-hover-bg' as any]:
        theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'
    }),
    [bg, border, text, icon, active, theme]
  );

  const shouldHideSidebarButton =
    location.pathname.startsWith('/caption-editor/') ||
    location.pathname.startsWith('/transcript-editor/');

  const STATUS_PATH = PATHS().STATUS;
  const isStatus = location.pathname === STATUS_PATH;

  const IconButton = ({
    label,
    children,
    onClick,
    isActive = false
  }: {
    label: string;
    children: React.ReactNode;
    onClick?: () => void;
    isActive?: boolean;
  }) => (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex h-9 w-9 items-center justify-center rounded-2xl transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
        theme === 'dark'
          ? 'focus-visible:ring-offset-gray-900'
          : 'focus-visible:ring-offset-white',
        isActive
          ? 'text-white bg-[var(--tn-active)]'
          : 'text-[var(--tn-icon)] hover:bg-[var(--tn-hover-bg)]'
      )}
    >
      {children}
    </button>
  );

  return (
    <nav className="w-full h-16" style={styleVars}>
      <div
        className={cn(
          'flex h-16 items-center gap-x-4',
          'px-3 sm:px-4 lg:px-8',
          'shadow-sm lg:shadow-none',
          css(styles.container)
        )}
      >
        {largeTabletAndAbove ? (
          <div className="flex items-center">
            <Logo isLink />
          </div>
        ) : (
          <>
            {!shouldHideSidebarButton ? (
              <IconButton label={t('open-sidebar')}>
                <Menu className="h-5 w-5" aria-hidden="true" />
              </IconButton>
            ) : (
              <div className="flex items-center">
                <Logo isLink />
              </div>
            )}
            <div
              className="h-6 w-px bg-[var(--tn-border)]/70 mx-1"
              aria-hidden="true"
            />
          </>
        )}

        <div className="flex-1" />

        <div className="flex items-center gap-x-3 sm:gap-x-4">
          {!IS_ON_PREM && (
            <Tooltip label={t('status')} placement="bottom">
              <IconButton
                label={t('status')}
                onClick={() => navigate(STATUS_PATH)}
                isActive={isStatus}
              >
                <ActivitySquare className="h-5 w-5" />
              </IconButton>
            </Tooltip>
          )}
          {!IS_ON_PREM && (
            <Tooltip label={t('feedback')} placement="bottom">
              <a
                href="https://feedback.scriptix.io/"
                target="_blank"
                rel="noreferrer noopener"
                aria-label={t('feedback')}
                title={t('feedback')}
                className={cn(
                  'inline-flex h-9 w-9 items-center justify-center rounded-2xl transition-colors',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
                  'text-[var(--tn-icon)] hover:bg-[var(--tn-hover-bg)]'
                )}
              >
                <MessageSquareMore className="h-5 w-5" />
              </a>
            </Tooltip>
          )}
          <div
            className="hidden lg:block h-5 w-px bg-[var(--tn-border)]/70"
            aria-hidden="true"
          />
          <Button
            onClick={() => navigate('/login')}
            label={t('login')}
            icon={<ExternalLink width={18} />}
            size="small"
            variant="light"
            className={cn(
              'h-9 px-3 font-semibold',
              'hover:bg-[var(--tn-hover-bg)] border border-[var(--tn-border)]'
            )}
          />
        </div>
      </div>
    </nav>
  );
}
